<script lang="ts">
export default {
  name: "MainFobizzNavbarSearch",
};
</script>

<script setup lang="ts">
import { useApiClient } from "@/composables/useApiClient";
import { usePlatformApiClient } from "@/composables/usePlatformApiClient";
import type { SearchResult } from "@/services/fobizzPlatformApi";
import type { Item, Material, Folder } from "@/services/fobizzToolsApi";
import Combobox from "@github/combobox-nav";
import { vOnClickOutside } from "@vueuse/components";
import debounce from "lodash.debounce";

const { t } = useI18n();
const {
  items: itemsService,
  materials: materialsService,
  folders: foldersService,
} = useApiClient();
const { apiClient: platformApiClient } = usePlatformApiClient();

const emit = defineEmits(["activated", "deactivated"]);

const inputRef = ref<HTMLInputElement>();
const listRef = ref<HTMLElement>();
const searchTerm = ref("");
const coursesResults = ref<SearchResult[]>([]);
const myResults = ref<(Item | Folder)[]>([]);
const galleryResults = ref<(Material | Folder)[]>([]);
const folderResults = ref<Folder[]>([]); // Results that are folders
const isLoading = ref(false);
const showDropdown = ref(false);
const isActive = ref(false);

let combobox: Combobox | null = null;

const start = () => {
  if (!inputRef.value || !listRef.value) {
    return;
  }

  if (combobox) {
    combobox.destroy();
  }

  combobox = new Combobox(inputRef.value, listRef.value);
  combobox.start();

  // item selected event
  listRef.value.addEventListener("combobox-commit", (event) => {
    if (event.target) {
      (event.target as HTMLElement).click();
    }
  });
};

const stop = () => {
  if (combobox) {
    combobox.destroy();
  }
};

onBeforeUnmount(() => {
  if (combobox) {
    combobox.destroy();
  }
});

const queryClient = useQueryClient();

const useFoldersQuery = () => {
  return useQuery(
    {
      queryKey: ["folders"],
      queryFn: async () => await foldersService.listFolders({ limit: 99 }),
      placeholderData: keepPreviousData,
    },
    queryClient
  );
};

const search = async (query: string) => {
  if (query.length > 0) {
    showDropdown.value = true;
    isLoading.value = true;

    const result0 = platformApiClient.searches.search({
      query: query,
      limit: 3,
    });

    const result1 = itemsService.searchItems({
      query: query,
    });
    const result2 = materialsService.listMaterials({
      query: query,
    });

    const { data: foldersQueryData } = useFoldersQuery();

    const combinedResult = await Promise.all([
      result0,
      result1,
      result2,
      foldersQueryData,
    ]);

    coursesResults.value = combinedResult[0].courses;
    myResults.value = combinedResult[1].collection;
    galleryResults.value = combinedResult[2].collection;

    // Remove duplicate items/materials
    if (myResults.value.length) {
      // If an item or material is in both my content and gallery, only show in my content
      if (galleryResults.value.length) {
        const myResultIds = new Set(myResults.value.map((result) => result.id));
        galleryResults.value = galleryResults.value.filter(
          (result) => !myResultIds.has(result.id)
        );
      }
    }

    // Folders can be results in current folder and my content search, too. (Not in gallery)
    folderResults.value =
      foldersQueryData.value?.collection.filter((folder) =>
        folder.name.includes(query)
      ) ?? ([] as Folder[]);

    // If a folder (as search result) is located in current folder, only show in current folder, not in my content
    if (folderResults.value.length) {
      myResults.value.unshift(...folderResults.value);
    }

    isLoading.value = false;

    if (
      coursesResults.value.length ||
      myResults.value.length ||
      galleryResults.value.length
    ) {
      start();
    }
  } else {
    showDropdown.value = false;
    stop();
    isActive.value = false;
    nextTick(() => {
      emit("deactivated");
    });
  }
};

const debouncedSearch = debounce((query: string) => search(query), 300);

const handleSearchInput = (value: string) => {
  activate();
  debouncedSearch(value);
};

const activate = () => {
  isActive.value = true;
  nextTick(() => {
    inputRef.value?.focus();
  });
  emit("activated");
};

const handleBlur = () => {
  if (searchTerm.value.length === 0) {
    stop();
    isActive.value = false;
    nextTick(() => {
      emit("deactivated");
    });
  }
};

const isEmpty = computed(
  () =>
    coursesResults.value.length === 0 &&
    myResults.value.length === 0 &&
    galleryResults.value.length === 0
);

const clearAndClose = () => {
  searchTerm.value = "";
  stop();
  isActive.value = false;
  showDropdown.value = false;
  emit("deactivated");
};
</script>

<template>
  <div
    v-on-click-outside="clearAndClose"
    class="main-fobizz-navbar-search"
    :class="{
      'main-fobizz-navbar-search--active': isActive,
    }"
  >
    <div class="dropdown">
      <form>
        <div class="input-group">
          <MainFobizzNavbarIconButton
            v-tooltip.bottom="t('mainFobizzNavbar.tooltips.search')"
            :label="t('mainFobizzNavbar.tooltips.search')"
            type="submit"
            icon-name="search"
            style="font-size: 1.1rem"
            @click.prevent="activate"
          />
          <input
            v-if="isActive"
            ref="inputRef"
            v-model="searchTerm"
            class="main-fobizz-navbar-search__input"
            type="search"
            pattern=".*\w+.*"
            autocomplete="off"
            :placeholder="t('mainFobizzNavbar.navbarSearch.placeholder')"
            :aria-label="t('mainFobizzNavbar.navbarSearch.placeholder')"
            @input="
              handleSearchInput(($event.target as HTMLInputElement).value)
            "
            @click="activate"
            @blur="handleBlur"
            @keyup.enter.prevent
          />
          <MainFobizzNavbarIconButton
            v-if="searchTerm"
            label="Clear search"
            class="main-fobizz-navbar-search__clear-btn"
            icon-name="x-circle-fill"
            @click="clearAndClose"
          />
        </div>
        <div
          class="dropdown-menu dropdown-menu-end main-fobizz-navbar-search__dropdown"
          :class="{ show: showDropdown }"
        >
          <div v-if="isLoading" class="d-flex justify-content-center p-2">
            <BaseSpinner size="md" />
          </div>
          <ul ref="listRef" role="listbox" class="results-list">
            <template
              v-if="!isLoading && coursesResults && coursesResults.length > 0"
            >
              <SearchResultsHeader
                :label="t('mainFobizzNavbar.navbarSearch.results.courses')"
                :search-term="searchTerm"
                url="/fortbildungen"
              />
              <div class="py-2">
                <SearchResultsItem
                  v-for="result in coursesResults"
                  :key="result.id"
                  :result="result"
                  :gallery="false"
                />
              </div>
            </template>

            <template v-if="!isLoading && myResults && myResults.length > 0">
              <SearchResultsHeader
                :label="t('mainFobizzNavbar.navbarSearch.results.myItems')"
                :search-term="searchTerm"
              />
              <div class="py-2">
                <SearchResultsItem
                  v-for="result in myResults.slice(0, 3)"
                  :key="result.id"
                  :result="result"
                  :gallery="false"
                />
              </div>
            </template>
            <template
              v-if="!isLoading && galleryResults && galleryResults.length > 0"
            >
              <SearchResultsHeader
                gallery
                :label="t('mainFobizzNavbar.navbarSearch.results.materials')"
                :search-term="searchTerm"
              />
              <div class="pt-2">
                <SearchResultsItem
                  v-for="result in galleryResults.slice(0, 3)"
                  :key="result.id"
                  :result="result"
                  :gallery="true"
                />
              </div>
            </template>
            <template v-else-if="!isLoading && isEmpty">
              <div class="result-empty">
                {{ t("mainFobizzNavbar.navbarSearch.results.empty") }}
              </div>
            </template>
          </ul>
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.main-fobizz-navbar-search {
  position: relative;
  display: flex;
  justify-content: flex-end;
  flex: 1 1;

  &__input {
    position: relative;
    appearance: none;
    border: none;
    outline: none;
    color: $secondary;
    background-color: $gray-100;
    width: 0;
    padding: 0;
    transition: width 0.2s ease-in-out;
  }

  .input-group {
    justify-content: flex-end;
    flex-wrap: nowrap;
  }

  &__clear-btn {
    color: $secondary;
    background-color: $gray-100;
  }

  &__dropdown {
    top: 2.3rem;
    width: 100%;
  }
}

.main-fobizz-navbar-search--active {
  .main-fobizz-navbar-search__input {
    width: 500px;

    @include media-breakpoint-down(lg) {
      width: 350px;
    }

    @include media-breakpoint-down(md) {
      width: 250px;
    }

    @include media-breakpoint-down(sm) {
      width: 140px;
    }
  }

  .main-fobizz-navbar__link {
    height: fit-content;
  }

  .main-fobizz-navbar__nav-icon {
    color: $secondary;
    background-color: $gray-100;

    &:hover::before {
      display: none;
    }
  }

  /* clears the ‘X’ from Chrome */
  input[type="search"]::-webkit-search-decoration,
  input[type="search"]::-webkit-search-cancel-button,
  input[type="search"]::-webkit-search-results-button,
  input[type="search"]::-webkit-search-results-decoration {
    display: none;
  }
}

.results-list {
  margin: 0;
  margin-top: -1px;
  padding: $spacer-1 0;
  list-style: none;

  &:empty {
    display: none;
  }
}

.result-empty {
  text-align: center;
  color: $secondary;
  font-size: $font-size-sm;
  font-weight: $font-weight-semibold;
}
</style>
